/**
 * ThemeOverride React context.
 *
 * Provides the current ThemeOverrideConfig and resolved CSS variable
 * overrides to descendant components.
 *
 * Unlike EntityForm/DataList contexts, the consumer hook returns `null`
 * when used outside a provider — components should work fine without
 * a theme override scope.
 */

import { createContext, useContext } from 'react';
import type { ThemeOverrideContextValue } from '../types';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

/**
 * React context for ThemeOverride.
 * Initialised as `null` — the provider is set up in ThemeOverrideRoot.
 */
export const ThemeOverrideContext =
  createContext<ThemeOverrideContextValue | null>(null);

ThemeOverrideContext.displayName = 'ThemeOverrideContext';

// ---------------------------------------------------------------------------
// Consumer Hook
// ---------------------------------------------------------------------------

/**
 * Access the current ThemeOverride context.
 *
 * Returns `null` when used outside any ThemeOverride — does NOT throw.
 * This is intentional: components should render normally without a
 * theme override scope.
 */
export function useThemeOverrideContext(): ThemeOverrideContextValue | null {
  return useContext(ThemeOverrideContext);
}
